<template>
  <section id="items-list" class="flex flex-col gap-5">
    <div id="actions" class="flex justify-between items-center gap-3">
      <p class="text-xl">{{ "Items*" }}</p>
      <div>
        <Button
          v-if="questionStore.options && questionStore.options?.length < 5"
          label="Adicionar Opções"
          icon="pi pi-plus"
          class="btn-simple"
          @click.prevent="openDialog('options')"
        />
        <Button
          v-if="questionStore.items && questionStore.items?.length < 5"
          label="Criar Item"
          class="btn-outline"
          @click.prevent="openDialog('items')"
        />
      </div>
    </div>
  </section>
  <section id="items" class="mt-5">
    <div
      id="info-items"
      class="flex justify-between items-center gap-3 bg-h-neutral-200 p-5 shadow rounded font-bold"
    >
      <p>{{ "Items criados" }}</p>
      <p
          :class="[
            { 'text-h-error-600': items.length === 0 },
            { 'text-h-warning-600': items.length >= 1 && items.length < 3 },
            { 'text-h-success-600': items.length >= 3 },
          ]"
        >
          {{ items?.length }} {{ " / 5 itens" }}
        </p>
    </div>

    <EmptyData
      v-if="items?.length === 0"
      :data="items?.length === 0"
      :label="emptyStateItems.description"
      :image="emptyStateItems.image"
      imgSize="h-40 my-10"
      msgSize="lg:w-[350px] -mt-14"
    />

    <div v-else>
      <div v-for="(item, index) in items" :key="index">
        <ItemCard :item="item" :item-index="index" @edit-item="onEditItems" />
      </div>
    </div>
  </section>

  <section id="options" class="mt-10 pb-32">
    <div
      id="info-options"
      class="flex justify-between items-center gap-3 bg-h-neutral-200 p-5 shadow rounded font-bold"
    >
      <p>{{ "Opções de resposta" }}</p>
      <p>{{ questionStore.getTotalOptions }} {{ " opções" }}</p>
    </div>

    <EmptyData
      v-if="questionStore.getTotalOptions === 0"
      :data="questionStore.getTotalOptions === 0"
      :label="emptyStateOptions.description"
      :image="emptyStateOptions.image"
      imgSize="h-40 my-10"
      msgSize="lg:w-[500px] -mt-14"
    />

    <div v-else>
      <ResponseCard
        :responses="questionStore.responses"
        v-if="questionStore.responses?.length > 0"
      />
      <OptionsCard
        v-if="questionStore.options?.length > 0"
        :options="questionStore.options"
        @edit-options="onEditOptions"
      />
    </div>
  </section>

  <Dialog
    v-model:visible="open"
    :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
    :style="{ width: '45vw' }"
    :modal="true"
    :draggable="false"
    @hide="closeDialog(dialogType)"
  >
    <template #header>
      <p class="text-2xl text-center w-full">
        {{ dialogType === "items" ? "Criar Item" : "Adicionar opções" }}
      </p>
    </template>

    <FormItems
      v-if="dialogType === 'items'"
      @on-close="closeDialog"
      :isEditing="isEditing.items"
    />
    <FormOptions
      v-if="dialogType === 'options'"
      @on-close="closeDialog"
      :isEditing="isEditing.options.action"
    />
  </Dialog>
</template>

<script lang="ts">
import { useEditorStore } from "@/store/editor";
import { defineComponent, onMounted, ref } from "vue";
import FormItems from "./FormItems.vue";
import FormOptions from "./FormOptions.vue";
import { useQuizQuestionsDragNDropStore } from "@/store/quiz-question-drag";
import ItemCard from "./ItemCard.vue";
import ResponseCard from "./ResponseCard.vue";
import OptionsCard from "./OptionsCard.vue";
import { useRoute } from "vue-router";
import { restoreItemsAndOptions } from "./dragAndDrop.utils";

export default defineComponent({
  name: "DragAndDropQuestion",
  components: { FormItems, FormOptions, ItemCard, ResponseCard, OptionsCard },
  data() {
    return {
      emptyStateItems: {
        image: "empty-items.svg",
        description:
          "Você pode cadastrar até 5 itens por questão. Clique em “Criar item” para começar.",
      },
      emptyStateOptions: {
        image: "empty-options.svg",
        description:
          "As respostas de cada item serão listadas como opções de resposta. Caso queira adicionar opções extras para deixar a questão mais desafiadora, clique no botão “Adicionar opções”.",
      },
    };
  },
  setup() {
    const open = ref(false);
    const dialogType = ref<"items" | "options">("items");
    const questionStore = useQuizQuestionsDragNDropStore;
    const items = useQuizQuestionsDragNDropStore.items;
    const isEditing = {
      items: { action: false, index: 0 },
      options: { action: false },
    };
    const options = ref([]);
    const route = useRoute();
    function openDialog(type: "items" | "options"): void {
      open.value = true;
      dialogType.value = type;
      isEditing.items.action = false;
      isEditing.items.index = questionStore.items.length;
      isEditing.options.action = false;
    }

    function closeDialog(type: "items" | "options"): void {
      open.value = false;

      if (type == "items") {
        useEditorStore.setEditor(null);
        useEditorStore.setContent(null);
      }
    }
    function onEditOptions(): void {
      open.value = true;
      dialogType.value = "options";
      Object.assign(isEditing.options, { action: true });
    }
    function onEditItems(index: number): void {
      open.value = true;
      dialogType.value = "items";
      Object.assign(isEditing.items, { action: true, index: index });
    }

    onMounted(() => {
      if (route.params.id) {
        restoreItemsAndOptions(questionStore);
      }
    });

    return {
      open,
      items,
      options,
      dialogType,
      isEditing,
      openDialog,
      closeDialog,
      onEditOptions,
      onEditItems,
      questionStore,
    };
  },
});
</script>
